// sensorUi.js
// Small shared UI kit for the alphatech sensor dashboards (Current / Temperature).
// Deliberately built in alphatech's flat "enterprise slate" language — plain
// divs + inline styles + react-icons — so it looks nothing like Aquabrim's
// gradient Material-UI cards while presenting the same data.
import React from 'react';

export const SLATE = {
  ink: '#0f172a',      // primary text
  ink2: '#334155',     // secondary text
  muted: '#64748b',    // tertiary
  faint: '#94a3b8',    // labels
  line: '#e2e8f0',     // borders
  line2: '#f1f5f9',    // hairlines
  bg: '#f8fafc',       // page background
  card: '#ffffff',
};

// Phase + signal colors, shared across metric tiles and charts.
export const PHASE = {
  R: '#e11d48', // rose
  Y: '#d97706', // amber-700
  B: '#2563eb', // blue
  active: '#0ea5e9',
  reactive: '#7c3aed',
  avg: '#0f766e',
};

export const cardShell = {
  background: SLATE.card,
  border: `1px solid ${SLATE.line}`,
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 6px 16px rgba(15,23,42,0.05)',
};

// A titled section card with an icon chip on the left and optional right slot.
export const SectionCard = ({ icon, title, subtitle, right, children, bodyStyle }) => (
  <div style={{ ...cardShell, overflow: 'hidden', marginBottom: 16 }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '14px 16px',
        borderBottom: `1px solid ${SLATE.line2}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: SLATE.bg,
            border: `1px solid ${SLATE.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: SLATE.ink,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: SLATE.ink, lineHeight: 1.25 }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: '0.68rem', color: SLATE.faint, fontWeight: 600 }}>{subtitle}</div>
          )}
        </div>
      </div>
      {right}
    </div>
    <div style={{ padding: 16, ...bodyStyle }}>{children}</div>
  </div>
);

// A single metric tile: uppercase label, big tabular value, unit, optional sub.
export const MetricTile = ({ label, value, unit, accent = SLATE.ink, sub }) => (
  <div
    style={{
      background: SLATE.card,
      border: `1px solid ${SLATE.line}`,
      borderRadius: 12,
      borderLeft: `3px solid ${accent}`,
      padding: '12px 14px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: 4,
    }}
  >
    <div
      style={{
        fontSize: '0.62rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: SLATE.faint,
        fontWeight: 700,
      }}
    >
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span
        style={{
          fontSize: '1.4rem',
          fontWeight: 800,
          color: SLATE.ink,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
      {unit && <span style={{ fontSize: '0.72rem', fontWeight: 700, color: SLATE.muted }}>{unit}</span>}
    </div>
    {sub && <div style={{ fontSize: '0.64rem', color: SLATE.faint, fontWeight: 600 }}>{sub}</div>}
  </div>
);

// A compact pill toggle group.
export const PillTabs = ({ value, onChange, options }) => (
  <div
    style={{
      display: 'flex',
      gap: 2,
      padding: 3,
      background: SLATE.bg,
      border: `1px solid ${SLATE.line}`,
      borderRadius: 10,
    }}
  >
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            border: 'none',
            cursor: 'pointer',
            borderRadius: 8,
            padding: '5px 14px',
            fontSize: '0.76rem',
            fontWeight: 700,
            background: active ? SLATE.ink : 'transparent',
            color: active ? '#fff' : SLATE.muted,
            transition: 'all 0.15s',
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

// A clickable series chip used to toggle chart lines.
export const SeriesChip = ({ label, active, color, onClick }) => (
  <button
    onClick={onClick}
    style={{
      cursor: 'pointer',
      borderRadius: 8,
      padding: '3px 10px',
      fontSize: '0.72rem',
      fontWeight: 700,
      border: `1.5px solid ${color}`,
      background: active ? color : 'transparent',
      color: active ? '#fff' : color,
      transition: 'all 0.15s',
    }}
  >
    {label}
  </button>
);
